import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faLayerGroup,
    faGripVertical,
    faMinus,
    faOutdent,
} from '@fortawesome/free-solid-svg-icons';
import { plural } from '../utils';
import { useEscapeKey } from '../hooks/useEscapeKey';

// Every file in a group — its own plus its (one level of) children's.
const groupFiles = (node) => [
    ...node.files,
    ...(node.children || []).flatMap((c) => c.files),
];

// The top-level node whose subtree contains `id` (itself or a child of it).
const topLevelOf = (tree, id) =>
    tree.find((n) => n.id === id || n.children.some((c) => c.id === id)) || null;

// `combined` is the alternative grouping: { title, description, action,
// rows: [{ name, trackCount, existing? }] }.
const FolderDropModal = ({ separateGroups, combined, onSeparate, onCombined, onCancel }) => {
    const [tree, setTree] = useState(() =>
        separateGroups.map((g) => ({ ...g, children: [] })));
    const [dragId, setDragId] = useState(null);
    const [overGroupId, setOverGroupId] = useState(null);

    useEscapeKey(onCancel);

    const droppedTotal = separateGroups.reduce((s, g) => s + g.files.length, 0);

    const rename = (id, name) =>
        setTree((prev) => prev.map((n) => (n.id === id ? { ...n, name } : n)));

    const remove = (id) => setTree((prev) => {
        if (prev.some((n) => n.id === id)) return prev.filter((n) => n.id !== id);
        return prev.map((n) => ({
            ...n,
            children: n.children.filter((c) => c.id !== id),
        }));
    });

    // Nest `sourceId`'s whole subtree as direct children of the dropped-on
    // group. Subtrees are flattened so nesting stays exactly one level deep.
    const nestInto = (sourceId, targetRowId) => setTree((prev) => {
        const target = topLevelOf(prev, targetRowId);
        if (!target) return prev;
        const sourceTop = prev.find((n) => n.id === sourceId);
        let movers;
        let next;
        if (sourceTop) {
            if (sourceTop.id === target.id) return prev;
            movers = [
                { id: sourceTop.id, name: sourceTop.name, files: sourceTop.files },
                ...sourceTop.children,
            ];
            next = prev.filter((n) => n.id !== sourceId);
        } else {
            const parent = prev.find((n) => n.children.some((c) => c.id === sourceId));
            if (!parent || parent.id === target.id) return prev;
            movers = [parent.children.find((c) => c.id === sourceId)];
            next = prev.map((n) => (n.id === parent.id
                ? { ...n, children: n.children.filter((c) => c.id !== sourceId) }
                : n));
        }
        return next.map((n) => (n.id === target.id
            ? { ...n, children: [...n.children, ...movers] }
            : n));
    });

    const unnest = (childId) => setTree((prev) => {
        const parent = prev.find((n) => n.children.some((c) => c.id === childId));
        if (!parent) return prev;
        const child = parent.children.find((c) => c.id === childId);
        return [
            ...prev.map((n) => (n.id === parent.id
                ? { ...n, children: n.children.filter((c) => c.id !== childId) }
                : n)),
            { id: child.id, name: child.name, files: child.files, children: [] },
        ];
    });

    const endDrag = () => { setDragId(null); setOverGroupId(null); };

    const handleSeparate = () => onSeparate(tree.map((n) => ({
        id: n.id,
        name: n.name.trim() || 'Untitled',
        files: groupFiles(n),
    })));

    const dragGroupId = dragId != null ? topLevelOf(tree, dragId)?.id : null;

    const dragProps = (id) => ({
        draggable: true,
        onDragStart: (e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id);
            setDragId(id);
        },
        onDragEnd: endDrag,
    });

    const dropProps = (rowId) => {
        const groupId = topLevelOf(tree, rowId)?.id;
        return {
            onDragOver: (e) => {
                if (dragId == null || groupId === dragGroupId) return;
                e.preventDefault();
                if (overGroupId !== groupId) setOverGroupId(groupId);
            },
            onDrop: (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dragId != null) nestInto(dragId, rowId);
                endDrag();
            },
        };
    };

    const renderChild = (child, groupId) => (
        <li
            key={child.id}
            className={`folder-drop-list-row nested${
                overGroupId === groupId ? ' merge-target' : ''
            }`}
            {...dropProps(child.id)}
        >
            <span className="folder-drop-corner">↳</span>
            <FontAwesomeIcon icon={faFolder} className="folder-drop-list-icon" />
            <span className="folder-drop-list-name">{child.name}</span>
            <span className="folder-drop-list-count">{plural(child.files.length, 'track')}</span>
            <button
                type="button"
                className="folder-drop-icon-btn"
                title="Pull out as its own project"
                onClick={() => unnest(child.id)}
            >
                <FontAwesomeIcon icon={faOutdent} />
            </button>
            <button
                type="button"
                className="folder-drop-icon-btn danger"
                title="Remove this folder"
                onClick={() => remove(child.id)}
            >
                <FontAwesomeIcon icon={faMinus} />
            </button>
        </li>
    );

    return (
        <div className="modal-backdrop" onClick={onCancel}>
            <div className="modal-card folder-drop-card" onClick={(e) => e.stopPropagation()}>
                <div className="new-project-eyebrow">IMPORT FOLDERS</div>
                <div className="new-project-title">{plural(droppedTotal, 'track')} dropped</div>
                <div className="folder-drop-sub">How should they be organized?</div>

                <div className="folder-drop-options">
                    <div className="folder-drop-option">
                        <div className="folder-drop-option-head">
                            <span className="folder-drop-option-icon">
                                <FontAwesomeIcon icon={faFolder} />
                            </span>
                            <div className="folder-drop-option-text">
                                <div className="folder-drop-option-title">Separate projects</div>
                                <div className="folder-drop-option-desc">
                                    Rename, or drag a folder onto another to nest it
                                </div>
                            </div>
                        </div>
                        <ul className="folder-drop-list">
                            {tree.map((node) => (
                                <React.Fragment key={node.id}>
                                    <li
                                        className={`folder-drop-list-row${
                                            dragId === node.id ? ' dragging' : ''
                                        }${overGroupId === node.id ? ' merge-target' : ''}`}
                                        {...dropProps(node.id)}
                                    >
                                        <span className="folder-drop-grip" {...dragProps(node.id)} title="Drag onto another folder to nest it">
                                            <FontAwesomeIcon icon={faGripVertical} />
                                        </span>
                                        <FontAwesomeIcon icon={faFolder} className="folder-drop-list-icon" />
                                        <input
                                            className="folder-drop-name-input"
                                            value={node.name}
                                            spellCheck={false}
                                            onChange={(e) => rename(node.id, e.target.value)}
                                        />
                                        <span className="folder-drop-list-count">
                                            {plural(groupFiles(node).length, 'track')}
                                        </span>
                                        <button
                                            type="button"
                                            className="folder-drop-icon-btn danger"
                                            title="Remove this folder"
                                            onClick={() => remove(node.id)}
                                        >
                                            <FontAwesomeIcon icon={faMinus} />
                                        </button>
                                    </li>
                                    {node.children.map((c) => renderChild(c, node.id))}
                                </React.Fragment>
                            ))}
                        </ul>
                        <button
                            className="btn-primary folder-drop-go"
                            onClick={handleSeparate}
                            disabled={!tree.length}
                        >
                            Create {plural(tree.length, 'project')}
                        </button>
                    </div>

                    <div className="folder-drop-option">
                        <div className="folder-drop-option-head">
                            <span className="folder-drop-option-icon">
                                <FontAwesomeIcon icon={faLayerGroup} />
                            </span>
                            <div className="folder-drop-option-text">
                                <div className="folder-drop-option-title">{combined.title}</div>
                                <div className="folder-drop-option-desc">{combined.description}</div>
                            </div>
                        </div>
                        <ul className="folder-drop-list">
                            {combined.rows.map((r, i) => (
                                <li key={i} className="folder-drop-list-row">
                                    <FontAwesomeIcon icon={faFolder} className="folder-drop-list-icon" />
                                    <span className="folder-drop-list-name">{r.name}</span>
                                    {r.existing && <span className="folder-drop-list-tag">current</span>}
                                    <span className="folder-drop-list-count">
                                        {r.existing ? '+' : ''}{plural(r.trackCount, 'track')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <button className="btn-primary folder-drop-go" onClick={onCombined}>
                            {combined.action}
                        </button>
                    </div>
                </div>

                <div className="new-project-actions">
                    <button type="button" className="btn-ghost folder-drop-cancel" onClick={onCancel}>
                        Cancel upload
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FolderDropModal;
